/**
 * Notification Sidebar — live notification bell + panel for marketing staff.
 * Polls every 15 seconds for near-real-time updates.
 */
import { api, router } from '@isg/ui-kit';

var pollInterval = null;
var isOpen = false;
var sidebarEl = null;

var BELL_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';

function esc(str) {
  var d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}

function timeAgo(isoStr) {
  var diff = Date.now() - new Date(isoStr).getTime();
  var mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  var days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  return new Date(isoStr).toLocaleDateString();
}

function getIcon(type) {
  var icons = {
    campaign_created: '\uD83D\uDCE2',
    campaign_status_changed: '\uD83D\uDD04',
    budget_approved: '\u2705',
    budget_rejected: '\u274C',
    plan_approved: '\uD83D\uDCCB',
    plan_status_changed: '\uD83D\uDD04',
    document_published: '\uD83D\uDCC4',
    performance_alert: '\u26A0\uFE0F',
    asset_uploaded: '\uD83D\uDDBC\uFE0F',
    system: '\u2139\uFE0F',
  };
  return icons[type] || '\uD83D\uDD14';
}

export function createNotificationBell() {
  var btn = document.createElement('button');
  btn.className = 'notification-bell-btn';
  btn.setAttribute('aria-label', 'Notifications');
  btn.innerHTML = BELL_SVG + '<span class="notification-badge" style="display:none">0</span>';
  btn.style.cssText = 'position:relative;background:none;border:none;cursor:pointer;padding:8px;color:var(--text-secondary);border-radius:var(--radius-md);';

  btn.addEventListener('click', function () {
    toggleSidebar();
  });

  // Start polling
  updateBadge(btn);
  pollInterval = setInterval(function () { updateBadge(btn); }, 15000);

  return btn;
}

async function updateBadge(btn) {
  try {
    var res = await api.get('/notifications/unread-count');
    var badge = btn.querySelector('.notification-badge');
    var count = res.unread_count || 0;
    badge.textContent = count > 99 ? '99+' : count;
    badge.style.display = count > 0 ? 'flex' : 'none';
  } catch (e) { /* ignore */ }
}

function toggleSidebar() {
  if (!sidebarEl) return;
  isOpen = !isOpen;
  sidebarEl.classList.toggle('open', isOpen);
  if (isOpen) loadNotifications();
}

async function loadNotifications() {
  if (!sidebarEl) return;
  var list = sidebarEl.querySelector('.notif-list');
  if (!list) return;

  list.innerHTML = '<div style="text-align:center;padding:2rem"><div class="spinner"></div></div>';

  try {
    var res = await api.get('/notifications?per_page=30');
    var items = res.notifications || [];

    if (items.length === 0) {
      list.innerHTML = '<div style="text-align:center;padding:2rem;color:var(--text-tertiary)">No notifications</div>';
      return;
    }

    list.innerHTML = items.map(function (n) {
      return '<div class="notif-item' + (n.is_read ? '' : ' unread') + '" data-id="' + n.id + '"' +
        (n.url ? ' data-url="' + esc(n.url) + '"' : '') + '>' +
        '<span class="notif-icon">' + getIcon(n.type) + '</span>' +
        '<div class="notif-content">' +
        '<div class="notif-title">' + esc(n.title) + '</div>' +
        (n.body ? '<div class="notif-body">' + esc(n.body) + '</div>' : '') +
        '<div class="notif-time">' + timeAgo(n.created_at) + '</div>' +
        '</div></div>';
    }).join('');

    list.querySelectorAll('.notif-item').forEach(function (el) {
      el.addEventListener('click', function () {
        var id = el.dataset.id;
        api.post('/notifications/' + id + '/read').catch(function () {});
        el.classList.remove('unread');
        if (el.dataset.url) router.navigate(el.dataset.url);
      });
    });
  } catch (e) {
    list.innerHTML = '<div style="text-align:center;padding:2rem;color:var(--red-600)">Failed to load</div>';
  }
}

export function createNotificationSidebar() {
  sidebarEl = document.createElement('div');
  sidebarEl.className = 'notification-sidebar';
  sidebarEl.innerHTML = `
    <div class="notif-header">
      <h3>Notifications</h3>
      <div style="display:flex;gap:var(--space-2)">
        <button class="btn btn-sm btn-ghost notif-mark-all">Mark all read</button>
        <button class="btn btn-sm btn-ghost notif-close">&times;</button>
      </div>
    </div>
    <div class="notif-list"></div>
  `;

  sidebarEl.querySelector('.notif-close').addEventListener('click', function () {
    isOpen = false;
    sidebarEl.classList.remove('open');
  });

  sidebarEl.querySelector('.notif-mark-all').addEventListener('click', async function () {
    await api.post('/notifications/read-all').catch(function () {});
    sidebarEl.querySelectorAll('.notif-item.unread').forEach(function (el) {
      el.classList.remove('unread');
    });
    // Update badge
    var badge = document.querySelector('.notification-badge');
    if (badge) { badge.style.display = 'none'; badge.textContent = '0'; }
  });

  return sidebarEl;
}
